import { OTHER_PAYMENT_OPTION_ID } from '@webapp/components/RequestableCreditCardSelect'
import { type ExpenseItemFormSchema } from '@webapp/modules/expense/components/ExpenseLineItemDrawer'
import { ExpensePaymentTypes } from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { type ExpenseTypeData } from '@webapp/modules/expense/entities/ExpenseItem/ExpenseTypeData'

export interface ExpenseItemPayload {
  merchant: string
  account: number
  localCurrency: number
  currency_rate_final: number
  reimburse: boolean
  expense_type: number
  expense_type_fields: Record<string, unknown> | null
  paymentType: number
  creditcard: Nullable<number>
  amount: string
  tax_rate: string
  tax_value: string
  tax_name: string
  tax_id: string | undefined
  tax_percentage: string | undefined
  tax_amount: string | undefined
  transDate: string
  attachment: string
  notes: string
}

export const mapExpenseTypeDataToFields = (
  typeData: ExpenseTypeData | null
): Record<string, unknown> | null => {
  if (typeData == null) return null

  switch (typeData.type) {
    case 'MEALS':
      return { MEALS: { tips: { value: String(typeData.data.tips) } } }
    case 'MILEAGE':
      return {
        MILEAGE: {
          mileage_rate: { value: String(typeData.data.mileageRate) },
          distance: { value: String(typeData.data.distance) },
          unit_of_distance: { value: typeData.data.unitOfDistance },
        },
      }
    case 'LABOUR':
      return {
        LABOUR: {
          hourly_rate: { value: String(typeData.data.hourlyRate) },
          hours_of_work: { value: String(typeData.data.hoursOfWork) },
        },
      }
    case 'AIRFARE':
      return {
        AIRFARE: {
          airline: { value: typeData.data.airline },
          ticket_number: { value: typeData.data.ticketNumber },
          departure_date: { value: typeData.data.departureDate },
          arrival_date: { value: typeData.data.arrivalDate },
        },
      }
    case 'OTHER':
    case 'LODGING':
      return { [typeData.type]: {} }
  }
}

export const mapExpenseItemFormSchemaToItemPayload = (
  item: ExpenseItemFormSchema
): ExpenseItemPayload => {
  let taxRate: string
  let taxValue: string
  let taxName: string
  let taxId: string | undefined
  let taxPercentage: string | undefined
  let taxAmount: string | undefined

  if (item.taxMode === 'percent') {
    taxRate = '%'
    taxName = '%'
    taxValue = item.taxValue || '0'
    taxId = undefined
    taxPercentage = (parseFloat(item.taxValue || '0') / 100).toFixed(4)
    taxAmount = undefined
  } else if (item.taxMode === 'dollar') {
    taxRate = '$'
    taxName = '$'
    taxValue = item.taxValue || '0'
    taxId = undefined
    taxPercentage = undefined
    taxAmount = item.taxValue || '0'
  } else {
    taxRate = String(item.taxId ?? '')
    taxName = item.taxName
    taxValue = item.taxValue || '0'
    taxId = String(item.taxId ?? '')
    taxPercentage = item.taxPercentage
    taxAmount = undefined
  }

  let paymentType: number
  let creditcard: Nullable<number>

  if (item.reimbursable || item.paymentMethod?.id === OTHER_PAYMENT_OPTION_ID) {
    paymentType = ExpensePaymentTypes.OTHER
    creditcard = null
  } else {
    paymentType = item.paymentType
    creditcard = item.paymentMethod?.id ?? null
  }

  return {
    merchant: item.merchant,
    account: item.account?.id ?? 0,
    localCurrency: item.currency.id,
    currency_rate_final: item.currency.rate,
    reimburse: item.reimbursable,
    expense_type: item.expenseType,
    expense_type_fields: mapExpenseTypeDataToFields(item.expenseTypeData),
    paymentType,
    creditcard,
    amount: String(parseFloat(item.amount || '0')),
    tax_rate: taxRate,
    tax_value: taxValue,
    tax_name: taxName,
    tax_id: taxId,
    tax_percentage: taxPercentage,
    tax_amount: taxAmount,
    transDate: item.transactionDate,
    attachment: item.attachment,
    notes: item.notes,
  }
}
